import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import HeroBackground from '../components/ui/HeroBackground';
import PromoBanner from '../components/ui/PromoBanner';
import AnimatedCounter from '../components/ui/AnimatedCounter';
import FAQSection from '../components/ui/FAQSection';
import Reveal from '../components/ui/Reveal';

// Letisztult vonalas SVG ikonok (Tabler-stílus), brand színnel
const FeatureIcons = {
  sword: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 3.5l6 6-9 9-3 1 1-3 9-9-2-2-9 9-1 3" />
      <path d="M3 21l3-3" />
    </svg>
  ),
  camera: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 8a2 2 0 012-2h2l1.5-2h7L17 6h2a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  ),
  gem: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3h12l4 6-10 12L2 9z" />
      <path d="M2 9h20M9 3l3 6-3 12M15 3l-3 6 3 12" />
    </svg>
  ),
  coin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v10M9.5 9.5c0-1.1 1.1-2 2.5-2s2.5.7 2.5 1.75-1.1 1.75-2.5 1.75-2.5.8-2.5 1.9S10.9 15 12.3 15s2.4-.6 2.4-1.5" />
    </svg>
  ),
  headset: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 13a8 8 0 0116 0" />
      <rect x="2.5" y="13" width="4" height="6" rx="1.5" />
      <rect x="17.5" y="13" width="4" height="6" rx="1.5" />
      <path d="M19.5 19v1a3 3 0 01-3 3h-3" />
    </svg>
  ),
};

const FEATURES = [
  { label: 'Auto Farming',  note: 'Waypoint autopilot',   icon: FeatureIcons.sword },
  { label: 'Camera Editor', note: 'Free zoom & angle',    icon: FeatureIcons.camera },
  { label: 'Auto Loot',     note: 'Filtered pickup',      icon: FeatureIcons.gem },
  { label: 'Auto Sell',     note: 'Rule-based vendoring', icon: FeatureIcons.coin },
  { label: '24/7 Support',  note: 'Discord & tickets',    icon: FeatureIcons.headset },
];

const TRUST = [
  'Instant key delivery',
  'HWID-locked license',
  'Always up to date',
];

const SHOWCASE_POINTS = [
  {
    title: 'Route groups',
    body: 'Chain several routes into one run — the bot handles zone transitions, difficulty selection and confirm dialogs on its own.',
  },
  {
    title: 'Visual waypoint editor',
    body: 'Record a path automatically or place waypoints by hand: move, clear area, kill target, open chest, enter gate, use teleport.',
  },
  {
    title: 'Recovers on its own',
    body: 'Stuck detection, blocked-target skipping and gate re-scanning keep long sessions running without babysitting.',
  },
];

function CheckIcon({ className = 'w-4 h-4' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
         strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

/* Egy termékkártya. A legjobb €/nap arányú csomag külön jelölést kap. */
function ProductCard({ product, isUpdating, index }) {
  const plans = product.plans || [];
  const shown = plans.slice(0, 3);

  const rated = shown.filter(p => p.days > 0 && p.days < 36000);
  const bestId = rated.length > 1
    ? rated.reduce((a, b) => (a.price / a.days <= b.price / b.days ? a : b)).id
    : null;

  return (
    <Reveal delay={index * 90}>
      <div className="card card-interactive p-6 flex flex-col h-full group">
        {product.image_url && (
          <div className="relative w-full h-44 rounded-lg bg-black/30 border border-white/[0.06]
                          mb-6 overflow-hidden">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-500 ease-smooth
                         group-hover:scale-[1.04]"
              onError={e => { e.target.style.display = 'none'; }}
            />
            <span className="absolute inset-0 bg-gradient-to-t from-gray-950/70 via-transparent to-transparent" />
          </div>
        )}

        <h3 className="font-display text-xl font-bold text-white mb-2">{product.name}</h3>
        <p className="text-sm text-gray-400 leading-relaxed mb-6 flex-1">{product.description}</p>

        <div className="surface-sunken divide-y divide-white/[0.05] mb-6">
          {shown.map(plan => (
            <div key={plan.id} className="flex items-center justify-between gap-3 px-3.5 py-2.5">
              <span className="flex items-center gap-2 text-sm text-gray-300 min-w-0">
                <span className="truncate">{plan.label}</span>
                {plan.id === bestId && (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded
                                   bg-brand-500/18 text-brand-200 border border-brand-400/25 flex-shrink-0">
                    Best value
                  </span>
                )}
              </span>
              <span className="text-brand-200 font-semibold tabular-nums flex-shrink-0">
                €{plan.price}
              </span>
            </div>
          ))}
          {plans.length > 3 && (
            <p className="px-3.5 py-2 text-xs text-gray-600">
              +{plans.length - 3} more {plans.length - 3 === 1 ? 'plan' : 'plans'}
            </p>
          )}
        </div>

        <Link
          to={isUpdating ? '#' : `/product/${product.id}`}
          className={`btn-primary w-full ${isUpdating ? 'opacity-45 cursor-not-allowed pointer-events-none' : ''}`}
        >
          {isUpdating ? 'Unavailable' : 'View Details'}
          {!isUpdating && (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round"
                 className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          )}
        </Link>
      </div>
    </Reveal>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [siteStatus, setSiteStatus] = useState('working');
  const [publicStats, setPublicStats] = useState({ totalUsers: 0, activeSubs: 0, usedKeys: 0 });

  useEffect(() => {
    api.get('/products')
      .then(res => {
        setProducts(res.data.products);
        setSiteStatus(res.data.siteStatus);
      })
      .catch(() => setError('Failed to load products.'))
      .finally(() => setLoading(false));

    api.get('/stats/public')
      .then(res => setPublicStats(res.data))
      .catch(() => {});
  }, []);

  const isUpdating = siteStatus === 'updating';

  const stats = [
    { value: publicStats.totalUsers, label: 'Registered users' },
    { value: publicStats.activeSubs, label: 'Active subscriptions' },
    { value: publicStats.usedKeys,   label: 'Keys activated' },
  ];

  return (
    <div>
      <PromoBanner />

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden px-4 pt-20 pb-24 sm:pt-28 sm:pb-32 text-center">
        <HeroBackground />

        <div className="relative max-w-3xl mx-auto">
          <h1 className="font-display text-display-sm sm:text-display lg:text-display-lg font-extrabold mb-6 animate-fade-up">
            <span className="text-gradient">The best bot</span>
            <br />
            <span className="text-gradient-brand animate-shine">on the market.</span>
          </h1>

          <p className="text-base sm:text-lg text-gray-400 mb-10 max-w-xl mx-auto leading-relaxed">
            The most advanced Drakensang Online bot available.
            Fully automated, and always up to date.
          </p>

          {isUpdating && (
            <div className="mb-8 px-5 py-3 rounded-xl text-sm flex items-center justify-center gap-2.5
                            bg-red-500/10 border border-red-500/25 text-red-300">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                   strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0">
                <path d="M12 9v4M12 17h.01" />
                <path d="M10.3 3.86l-8.18 14a1.5 1.5 0 001.3 2.14h16.16a1.5 1.5 0 001.3-2.14l-8.18-14a1.5 1.5 0 00-2.6 0z" />
              </svg>
              The service is currently being updated. Purchases and key activations are temporarily disabled.
            </div>
          )}

          <div className="flex items-center justify-center gap-3 flex-wrap">
            <a href="#products" className="btn-primary px-7 py-3 text-base">
              View Plans
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </a>
            {user ? (
              <Link to="/panel" className="btn-secondary px-7 py-3 text-base">Go to Dashboard</Link>
            ) : (
              <Link to="/register" className="btn-secondary px-7 py-3 text-base">Create Account</Link>
            )}
          </div>

          <div className="mt-10 flex items-center justify-center gap-x-6 gap-y-2 flex-wrap">
            {TRUST.map(t => (
              <span key={t} className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                <CheckIcon className="w-3.5 h-3.5 text-brand-400" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FEATURE STRIP ================= */}
      <section className="relative px-4 py-14 border-y border-white/[0.06] bg-black/20">
        {/* A hasábok száma a kártyák számához igazodik (öt), hogy a sáv széles
            képernyőn ne egy üres hellyel érjen véget. Mobilon az utolsó kártya
            teljes szélességű — így nem árván lóg a sor végén. */}
        <div className="max-w-6xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3
                        [&>*:last-child]:col-span-2 sm:[&>*:last-child]:col-span-1">
          {FEATURES.map((f, i) => (
            <Reveal key={f.label} delay={i * 60}>
              <div className="group h-full flex flex-col items-center text-center gap-3 px-3 py-5
                              rounded-xl border border-white/[0.05] bg-white/[0.02]
                              hover:border-brand-500/30 hover:bg-brand-500/[0.06]
                              transition-all duration-300 ease-smooth">
                <span className="w-11 h-11 rounded-xl flex items-center justify-center
                                 bg-brand-500/10 border border-brand-400/20 text-brand-300
                                 group-hover:bg-brand-500/18 group-hover:text-brand-200
                                 group-hover:shadow-glow-sm transition-all duration-300 ease-smooth">
                  <span className="w-5 h-5">{f.icon}</span>
                </span>
                <span className="text-xs font-semibold text-gray-200">{f.label}</span>
                <span className="text-[11px] text-gray-600 leading-tight -mt-1.5">{f.note}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="px-4 py-14">
        <div className="max-w-4xl mx-auto grid grid-cols-3 divide-x divide-white/[0.06]">
          {stats.map(s => (
            <div key={s.label} className="px-2 text-center">
              <div className="font-display text-3xl sm:text-4xl font-bold text-gradient-brand tabular-nums">
                <AnimatedCounter value={s.value} />
              </div>
              <div className="text-[11px] sm:text-xs text-gray-500 mt-2 uppercase tracking-wider">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= SHOWCASE ================= */}
      <section className="px-4 py-20 border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <span className="eyebrow mb-4">
              <span className="w-6 h-px bg-brand-400/60" />
              Autopilot
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
              Built for real farming runs
            </h2>
            <p className="text-gray-400 leading-relaxed mb-8">
              Not a click macro. A full navigation engine with recorded routes, gate handling
              and recovery — so a session survives the parts that usually break bots.
            </p>

            <div className="space-y-5">
              {SHOWCASE_POINTS.map(p => (
                <div key={p.title} className="flex gap-3.5">
                  <span className="flex-shrink-0 w-6 h-6 mt-0.5 rounded-md bg-brand-500/15
                                   border border-brand-400/25 text-brand-300
                                   flex items-center justify-center">
                    <CheckIcon className="w-3.5 h-3.5" />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-1">{p.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={120}>
            {/* Alkalmazás-keret a képernyőkép köré */}
            <div className="relative">
              {/* inset-0 (nem negatív), hogy mobilon ne told szét a lapot —
                  a blur amúgy is túlfut a dobozon, csak épp elrendezés nélkül. */}
              <div className="absolute inset-0 bg-brand-500/15 blur-3xl rounded-full pointer-events-none" />
              <div className="relative rounded-xl overflow-hidden border border-white/[0.09] bg-gray-900/80
                              shadow-card-lg backdrop-blur-sm">
                <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-white/[0.07] bg-black/30">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500/50" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/50" />
                  <span className="ml-2 text-[11px] font-mono text-gray-600">MortisEngine — Autopilot</span>
                </div>
                <img
                  src="/images/Screenshot_2.png"
                  alt="The MortisEngine autopilot tab with route groups and the waypoint editor"
                  className="w-full block"
                  loading="lazy"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section id="products" className="px-4 py-20 scroll-mt-nav border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="eyebrow mb-4">Pricing</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3 tracking-tight">
              Plans &amp; Pricing
            </h2>
            <p className="text-gray-400">Choose the plan that fits your needs.</p>
          </div>

          {loading && (
            <div className="flex justify-center py-10">
              <LoadingSpinner size="lg" label="Loading plans…" />
            </div>
          )}

          {error && !loading && (
            <div className="max-w-md mx-auto card p-6 text-center">
              <p className="text-sm text-red-300 mb-1">{error}</p>
              <p className="text-xs text-gray-500">Please refresh the page or try again in a moment.</p>
            </div>
          )}

          {!loading && !error && products.length === 0 && (
            <p className="text-center text-gray-500">No plans are available right now.</p>
          )}

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 items-stretch">
            {products.map((product, i) => (
              <ProductCard key={product.id} product={product} isUpdating={isUpdating} index={i} />
            ))}
          </div>
        </div>
      </section>

      <FAQSection />

      {/* ================= ZÁRÓ CTA ================= */}
      <section className="px-4 pb-24">
        <Reveal className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl border border-white/[0.09] p-10 sm:p-14 text-center">
            <span className="absolute inset-0 bg-hero-mesh pointer-events-none" />
            <span className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-brand-400/60 to-transparent" />

            <div className="relative">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-3 tracking-tight">
                Ready to stop grinding?
              </h2>
              <p className="text-gray-400 mb-8 max-w-md mx-auto">
                Create an account, pick a plan, and activate your key in the dashboard.
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {user ? (
                  <Link to="/panel" className="btn-primary px-7 py-3 text-base">Go to Dashboard</Link>
                ) : (
                  <Link to="/register" className="btn-primary px-7 py-3 text-base">Create Account</Link>
                )}
                <Link to="/docs" className="btn-secondary px-7 py-3 text-base">Read the docs</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
